/**
 * Sales > Targeting: every figure the tab shows, loaded from the real routes.
 *
 *   the metro's scored facilities   GET /api/financial/facilities/targeting (paged, 200 a page)
 *   its open dollars, largest first GET /api/financial/facilities/reimbursement-opportunity?order=desc
 *   and smallest first              ... ?order=asc (the "Skip for now" list)
 *   the selected facility           GET /api/aithora/crm/facilities/:id/territory (my snooze, warmth, EHR)
 *                                   GET /api/crm/deals?facilityId=&status=open (its open deal)
 *                                   GET /api/crm/companies/:id (only for a facility outside the metro)
 *                                   GET /api/aithora/crm/facilities/:id/network (Network view; real: false until data exists)
 *   Find a facility                 POST /api/aithora/crm/facilities/search
 *
 * The metro is the server's (the targeting route's default, or ?tgMetro=); nothing here
 * names one.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  crmCompany,
  crmDeals,
  crmFacilitySearch,
  tgNetwork,
  tgOpportunity,
  tgTargeting,
  tgTerritory,
  type CatalogRow,
  type CrmCompanyRecord,
  type TgNetwork,
  type TgTargetingResponse,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { useQuery } from '../../../useQuery';

/** The targeting route serves at most this many a page; the tab pages through the metro. */
export const TARGETING_PAGE = 200;
/** A metro larger than this is shown by its top facilities only. */
export const TARGETING_MAX = 2000;
/** The opportunity route's largest page. */
export const OPPORTUNITY_PAGE = 500;

/** The metro: its scored facilities and its open dollars (both ends). */
export function useMetroData(metroParam: string | null) {
  const { client, session } = useSession();
  const token = session?.token;

  const board = useQuery<TgTargetingResponse>(
    async (signal) => {
      const first = await tgTargeting(client, { query: { metro: metroParam ?? undefined, limit: TARGETING_PAGE, offset: 0 }, signal });
      const rows = [...first.facilities];
      while (rows.length < Math.min(first.total, TARGETING_MAX)) {
        const next = await tgTargeting(client, { query: { metro: first.meta.metro, limit: TARGETING_PAGE, offset: rows.length }, signal });
        if (!next.facilities.length) break;
        rows.push(...next.facilities);
      }
      return { ...first, facilities: rows };
    },
    [client, token, metroParam],
  );
  const metro = board.data?.meta.metro ?? null;
  const top = useQuery((signal) => tgOpportunity(client, { query: { metro: metro as string, sort: 'opportunity', order: 'desc', limit: OPPORTUNITY_PAGE }, signal }), [client, token, metro], {
    enabled: !!metro,
  });
  const low = useQuery((signal) => tgOpportunity(client, { query: { metro: metro as string, sort: 'opportunity', order: 'asc', limit: 200 }, signal }), [client, token, metro], {
    enabled: !!metro,
  });
  return { board, top, low };
}

/** The selected facility: my territory state, its open deal, and (Network view) who it runs on. */
export function useSelectedData(selId: string | null, wantNet: boolean) {
  const { client, session } = useSession();
  const token = session?.token;
  const territory = useQuery((signal) => tgTerritory(client, { params: { facilityId: selId as string }, signal }), [client, token, selId], { enabled: !!selId });
  const deal = useQuery((signal) => crmDeals(client, { query: { facilityId: selId as string, status: 'open', limit: 1 }, signal }), [client, token, selId], { enabled: !!selId });
  const net = useQuery(
    async (signal): Promise<TgNetwork | null> => {
      const r = await tgNetwork(client, { params: { facilityId: selId as string }, signal });
      return r && 'payer_mix' in r && Array.isArray(r.payer_mix) ? (r as TgNetwork) : null;
    },
    [client, token, selId],
    { enabled: !!selId && wantNet },
  );
  return { territory, deal, net };
}

/** A facility the metro lists do not hold (a search hit or a signal from elsewhere). */
export function useOutsideFacility(id: string | null, known: boolean) {
  const { client, session } = useSession();
  return useQuery<CrmCompanyRecord>((signal) => crmCompany(client, { params: { facilityId: id as string }, signal }), [client, session?.token, id], { enabled: !!id && !known });
}

/** Find a facility: the catalog's own search, after a short pause in typing. */
export function useFacilitySearch(q: string) {
  const { client } = useSession();
  const [hits, setHits] = useState<CatalogRow[]>([]);
  const term = q.trim();
  useEffect(() => {
    if (term.length < 2) {
      setHits([]);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      crmFacilitySearch(client, { body: { filters: { q: term }, limit: 6 }, signal: ctrl.signal })
        .then((r) => setHits(r.rows.slice(0, 6)))
        .catch(() => setHits([]));
    }, 250);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [client, term]);
  return useMemo(() => (term.length < 2 ? [] : hits), [term, hits]);
}
